import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { BrowserNtfNavigationService } from 'src/app/shared/services/browser-ntf/browser-ntf-navigation.service';

/** Data payload used to render an in-app browser notification toast. */
export interface BrowserNtfToastData {
  title: string;
  body: string;
  url?: string | null;
  estateId?: string | null;
  estateName?: string | null;
  notificationId?: string | null;
  createDate?: number | null;
  onOpenDetails?: (notificationId: string) => void;
}

@Component({
  selector: 'app-browser-ntf-toast',
  templateUrl: './browser-ntf-toast.component.html',
  styleUrls: ['./browser-ntf-toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BrowserNtfToastComponent implements OnInit, OnDestroy {
  public timeLabelKey: string | null = 'browserNtf.toast.now';
  public formattedDateTime: string = '';

  private timerId: number | null = null;
  private notificationCreateDate: number = Date.now();

  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public data: BrowserNtfToastData,
    private snackBarRef: MatSnackBarRef<BrowserNtfToastComponent>,
    private cdr: ChangeDetectorRef,
    private navigationSrv: BrowserNtfNavigationService
  ) {}

  ngOnInit(): void {
    this.notificationCreateDate = this.data.createDate ?? Date.now();
    this.updateTimeLabel();
    this.cdr.markForCheck();

    this.timerId = window.setInterval(() => {
      this.updateTimeLabel();
      this.cdr.markForCheck();
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.timerId !== null) {
      window.clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  /**
   * Closes currently visible toast.
   * @return {void}
   */
  public close(): void {
    this.snackBarRef.dismiss();
  }

  /**
   * Navigates user to notification details and closes toast.
   * @return {void}
   */
  public openDetails(): void {
    if (this.data.notificationId) {
      this.data.onOpenDetails?.(this.data.notificationId);
    }

    if (this.data.url) {
      this.navigationSrv.navigateToNotificationUrl(this.data.url, this.data.estateId ?? undefined);
    }
    this.close();
  }

  /**
   * Updates relative/absolute time label shown in toast header.
   * @return {void}
   */
  private updateTimeLabel(): void {
    const elapsedSeconds = Math.floor((Date.now() - this.notificationCreateDate) / 1000);
    if (elapsedSeconds < 60) {
      this.timeLabelKey = 'browserNtf.toast.now';
      this.formattedDateTime = '';
      return;
    }

    this.timeLabelKey = null;
    this.formattedDateTime = this.formatDateTime(this.notificationCreateDate);
  }

  /**
   * Formats timestamp to locale date-time string shown in toast.
   * @param {number} value
   * @return {string}
   */
  private formatDateTime(value: number): string {
    try {
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(value));
    } catch {
      return new Date(value).toLocaleString();
    }
  }
}
